import { observable, action } from "mobx";
import Autobind from "autobind-decorator";
import RootStore from "./rootStore";
import SeriesListItemModel from "models/seriesListItemModel";
import math from "core/math";
import Cache from "core/cache";
import Config from "config";
import Helper from "helper";
import Constants from "constants/index";

/**
 * @class SynchronizationStore
 * @description 요청에 따른 Study 목록을 반환
 */
@Autobind
class SeriesListStore {
  _rootStore: RootStore; /// rootStore 객체. 다른 Store를 연계할 때 사용

  @observable
  _isShowSeriesList = true;
  @observable
  _activeSeriesUniqueID = "";
  @observable
  _thumbnailFrameType = "mid";
  @observable
  _seriesListMap: Map<string, SeriesListItemModel> = new Map();

  constructor(rootStore: RootStore) {
    // root store 설정
    this._rootStore = rootStore;

    ///
    /// 썸네일 표시 프레임 설정 유효성 검사
    ///
    let thumbnailSlice = Config.thumbnail.slice || "mid"; // default - "first" 첫번째 프레임

    if (Helper.Validator.isString(thumbnailSlice) === false) {
      // 설정 값이 문자열이 아닌경우, 기본 "first" 설정(첫번째 프레임)
      thumbnailSlice = "mid";
    } else if (
      Helper.Validator.contains(
        Constants.Strings.ThumbnailSlice,
        thumbnailSlice.toLowerCase() == false
      )
    ) {
      // 설정 값이 유효값이 아니라면, 기본 "first" 설정(첫번째 프레임)
      thumbnailSlice = "mid";
    }

    this._thumbnailFrameType = thumbnailSlice.toLowerCase();
  }

  /**
   * @description SeriesListSection 영역 활성화 여부 반환
   * @function getIsOpen
   * @return true - SeriesListSection 활성화
   *         false - SeriesListSection 활성화
   */
  getIsOpen() {
    return this._isShowSeriesList;
  }
  /**
   * @description SeriesListSection 영역 활성화 설정
   * @function setIsOpen
   * @param isShow - true - SeriesListSection 활성화
   *                 false - SeriesListSection 활성화
   */
  @action
  setIsOpen(isShow: boolean) {
    Helper.Log.Dev(
      `@action : seriesListStore::setIsOpen. seriesID : ${isShow}`
    );
    this._isShowSeriesList = isShow;
  }

  /**
   * @description SeriesListSection 영역 활성/비활성화 토글
   * @function toggleIsOpen
   */
  @action
  toggleIsOpen() {
    Helper.Log.Dev(`@action : seriesListStore::toggleIsOpen.`);
    this._isShowSeriesList = !this._isShowSeriesList;
  }

  /**
   * @description 활성화된 Series의 Unique ID 반환
   * @function getActiveSeriesItem
   * @return Series Unique ID
   */
  getActiveSeriesItem() {
    return this._activeSeriesUniqueID;
  }
  /**
   * @description 특정 Series 항목 활성화
   * @function setActiveSeriesItem
   * @param seriesUniqueID 활성화 대상 항목의 Series Unique ID
   */
  @action
  setActiveSeriesItem(seriesUniqueID: string) {
    Helper.Log.Dev(
      `@action : seriesListStore::setActiveSeriesItem. seriesUniqueI=${seriesUniqueID}`
    );
    this._activeSeriesUniqueID = seriesUniqueID;
  }
  /**
   * @description Series Item 추가
   * @function addSeriesItem
   * @param seriesUniqueID Series Unique ID
   */
  @action
  addSeriesItem(seriesUniqueID: string) {
    if (this._seriesListMap.has(seriesUniqueID) === true) {
      return;
    }
    let seriesListItem: SeriesListItemModel = new SeriesListItemModel(
      seriesUniqueID
    );
    this._seriesListMap.set(seriesUniqueID, seriesListItem);
    Helper.Log.Dev(
      `@action : seriesListStore::addSeriesItem. seriesID : ${seriesUniqueID} leastItems=${this._seriesListMap.size}`
    );
  }
  /**
   * @description Series Item 추가
   * @function delSeriesItem
   * @param seriesUniqueID Series Unique ID
   */
  @action
  delSeriesItem(seriesUniqueID: string) {
    if (this._seriesListMap.has(seriesUniqueID) === false) {
      return;
    }
    this._seriesListMap.delete(seriesUniqueID);
    Helper.Log.Dev(
      `@action : seriesListStore::delSeriesItem. seriesID : ${seriesUniqueID} leastItems=${this._seriesListMap.size}`
    );
  }
  /**
   * @description Thumbnail에 표시되는 Frame 번호 반환
   * @function getThumbnailFrameNumber
   * @param seriesUniqueID Series Unique ID
   */
  getThumbnailFrameNumber(seriesUniqueID: string) {
    if (this._seriesListMap.has(seriesUniqueID) === false) {
      return -1;
    }
    return (this._seriesListMap.get(seriesUniqueID) as SeriesListItemModel)
      .thumbnailFrame;
  }
  /**
   * @description Thumbnail에 표시되는 Frame 번호 반환
   * @function getThumbnailFrameNumber
   * @param seriesUniqueID Series Unique ID
   */
  @action
  updateThumbnails() {
    Helper.Log.Dev(
      "@action : seriesListStore::updateThumbnail. type : " +
        this._thumbnailFrameType
    );

    const metadata = new Cache.Metadata();
    if (this._thumbnailFrameType === "first") {
      this._seriesListMap.forEach((seriesItem) => {
        seriesItem.thumbnailFrame = 0;
      });
    } else if (this._thumbnailFrameType === "mid") {
      this._seriesListMap.forEach((seriesItem) => {
        const frames: number = metadata.getNumOfFrame(seriesItem.uniqueID);
        const midFrame = math.range.clip(
          Math.floor((frames + 1) / 2),
          0,
          frames - 1
        );
        seriesItem.thumbnailFrame = midFrame;
      });
    } else if (this._thumbnailFrameType === "last") {
      this._seriesListMap.forEach((seriesItem) => {
        const frames: number = metadata.getNumOfFrame(seriesItem.uniqueID);
        seriesItem.thumbnailFrame = frames - 1;
      });
    }
  }
}

export default SeriesListStore;
